/**
 * How the room's cards are changed from their fields, whichever layout draws the fields: a word typed goes to the board,
 * and the file a field holds is picked in the one From Assets picker. Each change has its way back for Undo.
 */
import type { BoardCard, BoardState, LibraryAsset } from "../../../shared/types";
import { openAssets } from "../../assets/store";
import type { BoardHost } from "../host";
import { fileFace, type Moment, type Offer } from "./roomParts";

/** What a field of the room does to a card, and how that is taken back. */
export interface RoomEditing {
  typeOn: (id: string, patch: Record<string, unknown>) => void;
  typedOn: (id: string, back: (card: BoardCard) => Record<string, unknown>) => void;
}

/** A word typed onto a card goes where its card's own fields go: the board, and its save queue. */
export function roomEditing(host: BoardHost, onTyped?: (undo: (now: BoardState) => void) => () => void): RoomEditing {
  const typeOn = (id: string, patch: Record<string, unknown>) => host.dispatch({ type: "edit", id, patch: patch as never });
  return {
    typeOn,
    // Typing ended with a change: Undo puts the field back as it was.
    typedOn: (id, back) =>
      void onTyped?.((now) => {
        const card = now.room.find((c) => c.id === id);
        if (card) typeOn(id, back(card));
      }),
  };
}

interface FilePick {
  has: string;
  sub?: string;
  thumb?: string | null;
  onAdd: () => void;
  onOff?: () => void;
}

/** A handout's file field: the file it holds now and Assets to put another. */
export function handoutPick(m: Moment, assets: readonly LibraryAsset[], e: RoomEditing): FilePick {
  const face = m.kind === "handout" ? fileFace(assets, m.key) : null;
  return {
    has: m.kind === "handout" ? (face?.name ?? m.name) : "",
    sub: face?.sub,
    thumb: face?.pic,
    onAdd: () =>
      openAssets({
        fits: "handout",
        title: "The handout",
        cta: "Hand this out",
        onPick: (a) => {
          if (!a.key || m.kind !== "handout") return;
          const was = { name: m.name, key: m.key };
          e.typeOn(m.id, { name: a.name, key: a.key });
          e.typedOn(m.id, () => was);
        },
      }),
  };
}

/** The offer's file field: what they get when it is a download. */
export function offerFilePick(offer: Offer, assets: readonly LibraryAsset[], e: RoomEditing): FilePick {
  const face = offer.offerType === "download" ? fileFace(assets, offer.file) : null;
  return {
    has: offer.file ? (face?.name ?? offer.file.split("/").pop() ?? "A file is set") : "",
    sub: face?.sub,
    thumb: face?.pic,
    onAdd: () =>
      openAssets({
        fits: "handout",
        title: "The file they get",
        cta: "Give them this",
        onPick: (a) => {
          if (!a.key) return;
          const was = offer.file;
          e.typeOn(offer.id, { file: a.key });
          e.typedOn(offer.id, () => ({ file: was }));
        },
      }),
  };
}

/** The offer's video note field, with its frame and a way to take it off. */
export function videoNotePick(offer: Offer, assets: readonly LibraryAsset[], poster: string | null | undefined, mmss: (s: number) => string, e: RoomEditing): FilePick {
  const face = offer.videoNote ? fileFace(assets, undefined, offer.videoNote) : null;
  return {
    has: offer.videoNote ? (face?.name ?? `A note${offer.videoSec ? ` · ${mmss(offer.videoSec)}` : ""}`) : "",
    sub: face?.sub,
    thumb: poster,
    onAdd: () =>
      openAssets({
        fits: "video",
        title: "A short video note",
        cta: "Use this video",
        onPick: (a) => {
          if (!a.note_id) return;
          const was = { videoNote: offer.videoNote, videoSec: offer.videoSec };
          e.typeOn(offer.id, { videoNote: a.note_id, ...(a.duration_sec ? { videoSec: a.duration_sec } : {}) });
          e.typedOn(offer.id, () => was);
        },
      }),
    onOff: () => {
      const was = { videoNote: offer.videoNote, videoSec: offer.videoSec };
      e.typeOn(offer.id, { videoNote: undefined, videoSec: undefined });
      e.typedOn(offer.id, () => was);
    },
  };
}
